/**
 * The single front-end definition of a well-formed email address, mirroring
 * Easons.Core.Helpers.EmailValidation.Pattern. The server remains the authority; these are UX.
 *
 * #2922 - the domain is dot-separated labels rather than one class containing a dot. The
 * earlier `[a-zA-Z0-9.-]+\.` accepted "test@yahoo..ie", because the class absorbed the first
 * dot and the literal matched the second, and it accepted a domain starting with a dot.
 *
 * Unanchored: the HTML `pattern` attribute anchors implicitly, and EMAIL_REGEX adds anchors
 * for script use. Keep these in step with the C# constant - three separate copies of the old
 * pattern are how the defect survived its own fix.
 */
export const EMAIL_PATTERN =
  '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*\\.[a-zA-Z]{2,}';

export const EMAIL_REGEX = new RegExp(`^${EMAIL_PATTERN}$`);

export const ActionType = {
  SetBillingAddress: 'SET_BILLING_ADDRESS',
  SetClickAndCollectStoreId: 'SET_CLICK_AND_COLLECT_STORE_ID',
  SetDeliveryAddress: 'SET_DELIVERY_ADDRESS',
  SetDeliveryMethod: 'SET_DELIVERY_METHOD',
  SetDeliveryOption: 'SET_DELIVERY_OPTION',
  SetEmail: 'SET_EMAIL',
  SetGiftMessage: 'SET_GIFT_MESSAGE',
  SetIsGift: 'SET_IS_GIFT',
  SetMarketingOptIn: 'SET_MARKETING_OPT_IN',
} as const;

export const ButtonVariant = {
  FullWidth: 'fullwidth',
  Primary: 'primary',
  Secondary: 'secondary',
} as const;

export const DeliveryOption = {
  Collect: 'Collect',
  Ship: 'Ship',
} as const;

export const Endpoint = {
  AddDiscountCode: '/Checkout/AddDiscountCode',
  AddGiftCard: '/Checkout/AddGiftCard',
  AddToBasket: '/cart/additem',
  AddToBuyNowCart: '/cart/additemtobuynowcart',
  ApplyGiftCard: '/Checkout/ApplyGiftCard',
  ApplyLoyaltyPoints: '/Checkout/ApplyLoyaltyPoints',
  ClearDiscountCode: '/Checkout/ClearDiscountCode',
  ClearGiftCard: '/Checkout/ClearGiftCard',
  ClearLoyaltyPoints: '/Checkout/ClearLoyaltyPoints',
  CreateApplePayOrder: '/Checkout/CreateApplePayOrder',
  CreateBuyNowApplePayOrder: '/Checkout/CreateBuyNowApplePayOrder',
  CreateCreditCardOrder: '/Checkout/CreateCreditCardOrder',
  CreateGooglePayOrder: '/Checkout/CreateGooglePayOrder',
  CreateBuyNowGooglePayOrder: '/Checkout/CreateBuyNowGooglePayOrder',
  CreateQuickPayThreeDSecureOrder: '/Checkout/CreateQuickPayThreeDSecureOrder',
  CustomerStatus: '/cart/getcustomerstatus',
  EmailExists: '/Checkout/CheckEmailAddress',
  GetCart: '/Cart/GetCart',
  GetCartFullRefresh: '/Cart/GetCartFullRefresh',
  GetCounties: '/Checkout/GetCountryRegions',
  GetLoyaltyCardBalance: '/Checkout/GetLoyaltyCardBalance',
  GetShippingOptions: '/Checkout/GetShippingOptions',
  LoadGiftCards: '/Checkout/LoadGiftCards',
  Notify: '/Notifications/Create',
  Recommendations: '/endpoint-for-recommendations',
  RemoveItem: '/Cart/RemoveItem',
  SagePayGetMerchantSessionKey: '/SagePay/GetMerchantSessionKey',
  SendNoBalance: '/Checkout/CreateNoBalanceOrder',
  SendPayPalOrder: '/Checkout/SetupPayPalOrder',
  SetDeliveryCountry: '/Checkout/SetDeliveryCountry',
  SetShippingMethod: '/Checkout/SetShippingMethod',
  StoredDeliveryAddresses: '/Checkout/GetCustomerDeliveryAddresses',
  Subscribe: '/api/subscribe/newsletter',
  UpdateDeliveryAddress: '/Checkout/SetDeliveryAddress',
  UpdateItem: '/Cart/UpdateItem',
} as const;

export const FieldName = {
  AddressLine1: 'line1',
  AddressLine2: 'line2',
  City: 'city',
  Company: 'company',
  Country: 'countryCode',
  County: 'countyCode',
  FirstName: 'firstName',
  Id: 'id',
  LastName: 'lastName',
  Phone: 'phone',
  PostalCode: 'postalCode',
} as const;

export const FormLabel = {
  city: 'City',
  company: 'Company',
  countryCode: 'Country',
  countyCode: 'County',
  firstName: 'First name',
  lastName: 'Last name',
  line1: 'Address line 1',
  line2: 'Address line 2',
  phone: 'Phone',
  postalCode: 'Postal code',
} as const;

export const JsonScriptId = {
  Category: 'categoryData',
  Checkout: 'checkoutData',
  MyAccount: 'myAccountData',
  Page: 'pageData',
  Search: 'searchData',
  User: 'userData',
} as const;

export const PaymentOption = {
  CreditCard: 'creditcard',
  PayPal: 'paypal',
} as const;

export const PromotionStatus = {
  Fulfilled: 'Fulfilled',
  PartiallyFulfilled: 'PartiallyFulfilled',
} as const;

export const PurchaseAction = {
  BackOrder: 'BackOrder',
  CanPurchase: 'CanPurchase',
  None: 'None',
  Notify: 'Notify',
  PreOrder: 'PreOrder',
} as const;

export const QueryKey = {
  CategoryListing: 'category-listing',
  Counties: 'counties',
  CreateCreditCardOrder: 'createCreditCardOrder',
  CustomerStatus: 'customerStatus',
  EmailExists: 'emailExists',
  GetCart: 'getCart',
  GiftCards: 'giftCards',
  LoyaltyCardBalance: 'loyaltyCardBalance',
  SagePayGetMerchantSessionKey: 'sagePayGetMerchantSessionKey',
  ShippingOptions: 'shippingOptions',
  StoredDeliveryAddresses: 'storedDeliveryAddresses',
} as const;

// Please preserve this order so that error messages appear
// in the same order as form fields.
export const RequiredAddressFieldKeys = {
  CountryCode: 'countryCode',
  FirstName: 'firstName',
  LastName: 'lastName',
  AddressLine1: 'line1',
  City: 'city',
  PostalCode: 'postalCode',
  Phone: 'phone',
} as const;

// Please preserve this order so that error messages appear
// in the same order as checkout sections.
export const ValidationSectionKey = {
  Email: 'email',
  DeliveryOption: 'deliveryOption',
  ShippingAddress: 'shippingAddress',
  ShippingMethod: 'deliveryMethod',
  ClickAndCollectStoreId: 'storeSelection',
  BillingAddress: 'billingAddress',
  PaymentMethod: 'paymentMethod',
} as const;

export const ValidationMessage = {
  Address: 'Please complete all required ${addressType} address fields.',
  ErrorSummary:
    'The following needs attention before we can process your payment:',
  InvalidPhone:
    'Please enter a valid phone number: only +, -, (, ), and 8 to 15 numbers are allowed.',
  MissingField: 'Please fill in this field.',
  AddressLineTooLong: 'Address line(s) must not exceed 50 characters.',
  FirstNameTooLong: 'First name must not exceed 20 characters.',
  LastNameTooLong: 'Last name must not exceed 20 characters.',
  EmailTooLong: 'Email address must not exceed 80 characters.',
  PhoneTooLong: 'Phone number must not exceed 19 characters.',
  CityTooLong: 'City must not exceed 40 characters.',
  PostalCodeTooLong: 'Postal code must not exceed 10 characters.',
  SeparateBillingRequired:
    'You must specify a separate billing address when shipping to this country.',
} as const;
